import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import s from "../clinic.module.css";

const PAD = 10;

function measureTarget(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PAD,
    left: r.left - PAD,
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
    bottom: r.bottom + PAD,
    right: r.right + PAD,
    centerX: r.left + r.width / 2,
    centerY: r.top + r.height / 2,
  };
}

/**
 * Подсветка UI-элемента (setup-онбординг и product tour).
 * Не блокирует скролл страницы.
 */
export default function OnboardingSpotlight({
  targetSelector,
  title,
  description,
  stepLabel,
  onTargetActivate,
  onPrimary,
  onSecondary,
  onSkip,
  placement = "bottom",
  primaryLabel = "Открыть шаг",
  secondaryLabel = null,
  skipLabel = "Пропустить подсказки",
  footerSkipLabel = "Закрыть обучение",
  hint = "Нажмите на подсвеченную карточку или кнопку ниже",
  interceptTargetClick = true,
}) {
  const [hole, setHole] = useState(null);
  const [readyWithoutTarget, setReadyWithoutTarget] = useState(false);
  const noTarget = !targetSelector;

  const remeasure = useCallback(() => {
    if (noTarget) {
      setHole(null);
      setReadyWithoutTarget(true);
      return;
    }
    setHole(measureTarget(targetSelector));
    setReadyWithoutTarget(false);
  }, [noTarget, targetSelector]);

  useEffect(() => {
    remeasure();
    if (!noTarget) {
      const el = document.querySelector(targetSelector);
      if (el) {
        window.setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          remeasure();
        }, 80);
      }
    }
    const t = window.setInterval(remeasure, 250);
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [remeasure, targetSelector, noTarget]);

  useEffect(() => {
    if (noTarget || !targetSelector) return undefined;
    const el = document.querySelector(targetSelector);
    if (!el) return undefined;
    const prevZ = el.style.zIndex;
    const prevPos = el.style.position;
    const computed = window.getComputedStyle(el);
    if (computed.position === "static") el.style.position = "relative";
    el.style.zIndex = "10001";
    el.setAttribute("data-onboarding-highlight", "1");

    const onClick = (e) => {
      if (interceptTargetClick) {
        e.stopPropagation();
        e.preventDefault();
      }
      onTargetActivate?.();
    };
    el.addEventListener("click", onClick, true);

    return () => {
      el.removeEventListener("click", onClick, true);
      el.style.zIndex = prevZ;
      el.style.position = prevPos;
      el.removeAttribute("data-onboarding-highlight");
    };
  }, [targetSelector, onTargetActivate, interceptTargetClick, noTarget]);

  if (!noTarget && !hole) return null;
  if (noTarget && !readyWithoutTarget) return null;

  const handlePrimary = () => {
    if (onPrimary) onPrimary();
    else onTargetActivate?.();
  };

  let tooltipStyle;
  let arrowStyle = null;

  if (noTarget || placement === "center") {
    tooltipStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  } else if (placement === "right" && hole) {
    const left = Math.min(hole.right + 16, window.innerWidth - 300);
    const top = Math.max(16, Math.min(hole.centerY - 40, window.innerHeight - 200));
    tooltipStyle = { top, left, transform: "none" };
    arrowStyle = {
      left: -8,
      top: 28,
      transform: "rotate(-90deg)",
    };
  } else if (placement === "top" && hole) {
    tooltipStyle = {
      top: hole.top - 12,
      left: Math.min(Math.max(hole.centerX, 180), window.innerWidth - 180),
      transform: "translate(-50%, -100%)",
    };
    arrowStyle = {
      bottom: -8,
      left: "50%",
      transform: "translateX(-50%) rotate(180deg)",
    };
  } else if (hole) {
    tooltipStyle = {
      top: hole.bottom + 16,
      left: Math.min(Math.max(hole.centerX, 180), window.innerWidth - 180),
      transform: "translateX(-50%)",
    };
    arrowStyle = {
      top: -8,
      left: "50%",
      transform: "translateX(-50%)",
    };
  }

  return createPortal(
    <div className={s.onboardingSpotlightRoot} role="presentation">
      {hole ? (
        <div
          className={s.onboardingSpotlightDim}
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
          }}
          aria-hidden
        />
      ) : (
        <div className={s.onboardingSpotlightCenterDim} aria-hidden />
      )}
      <div
        className={s.onboardingSpotlightTooltip}
        style={tooltipStyle}
        role="dialog"
        aria-labelledby="onboarding-spotlight-title"
      >
        {arrowStyle ? (
          <span className={s.onboardingSpotlightArrow} style={arrowStyle} aria-hidden />
        ) : null}
        {stepLabel ? (
          <span className={s.onboardingSpotlightStep}>{stepLabel}</span>
        ) : null}
        <h4 id="onboarding-spotlight-title" className={s.onboardingSpotlightTitle}>
          {title}
        </h4>
        <p className={s.onboardingSpotlightDesc}>{description}</p>
        {hint ? <p className={s.onboardingSpotlightHint}>{hint}</p> : null}
        <div className={s.onboardingSpotlightActions}>
          <button
            type="button"
            className={s.onboardingSpotlightPrimary}
            onClick={handlePrimary}
          >
            {primaryLabel}
          </button>
          {secondaryLabel && onSecondary ? (
            <button
              type="button"
              className={s.onboardingSpotlightSecondary}
              onClick={onSecondary}
            >
              {secondaryLabel}
            </button>
          ) : null}
          <button
            type="button"
            className={s.onboardingSpotlightSkip}
            onClick={onSkip}
          >
            {skipLabel}
          </button>
        </div>
      </div>
      <div className={s.onboardingSpotlightFooter}>
        <button type="button" className={s.onboardingSpotlightFooterSkip} onClick={onSkip}>
          {footerSkipLabel}
        </button>
      </div>
    </div>,
    document.body,
  );
}
